import { SelectChangeEvent } from 'primeng/select';

import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  input,
  inject,
  OnInit,
  output,
  signal,
  computed,
  Component,
  InputSignal,
  WritableSignal,
  OutputEmitterRef,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';

import { SvgIcon } from '../svg-icon';

@Component({
  selector: 'app-pagination',
  templateUrl: './pagination.html',
  styleUrl: './pagination.scss',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'app-pagination',
  },
  imports: [ReactiveFormsModule, SvgIcon],
})
export class Pagination implements OnInit {
  private readonly router = inject(Router);

  private readonly route = inject(ActivatedRoute);

  private readonly formBuilder: FormBuilder = inject(FormBuilder);

  public elementsInPage: InputSignal<number> = input.required();

  public isFirstPage: InputSignal<boolean> = input.required();

  public isLastPage: InputSignal<boolean> = input.required();

  public currentPage: InputSignal<number> = input.required();

  public perPage: InputSignal<number> = input.required();

  public totalElements: InputSignal<number> = input.required();

  public totalPages: InputSignal<number> = input.required();

  public saveInQuery: WritableSignal<boolean> = signal(true);

  public pageChangeEvent: OutputEmitterRef<number> = output();

  public perPageChangeEvent: OutputEmitterRef<number> = output();

  protected itemsRange = computed(() => {
    const pageMaxItems = this.currentPage() * this.perPage();

    const to = pageMaxItems <= this.totalElements() ? pageMaxItems : this.totalElements();

    let from = to - this.elementsInPage() + 1;

    if (from < 1) {
      from = 1;
    }

    return {
      from,
      to,
    };
  });

  protected form!: FormGroup;

  protected perPageOptions = [
    {
      value: 15,
      label: 15,
    },

    {
      value: 30,
      label: 30,
    },

    {
      value: 50,
      label: 50,
    },

    {
      value: 100,
      label: 100,
    },
  ];

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      perPage: [this.perPage()],
    });
  }

  protected goToFirstPage() {
    this.goToPage(1);
  }

  protected goToLastPage() {
    this.goToPage(this.totalPages());
  }

  protected goToPreviewPage() {
    let previewPage = this.currentPage() - 1;
    if (previewPage < 1) {
      previewPage = 1;
    }

    this.goToPage(previewPage);
  }

  protected goToNextPage() {
    let nextPage = this.currentPage() + 1;
    if (nextPage > this.totalPages()) {
      nextPage = this.totalPages();
    }

    this.goToPage(nextPage);
  }

  protected goToPage(page: number) {
    if (this.saveInQuery()) {
      this.saveParamsInQuery({
        page: page.toString(),
      });
    }

    this.pageChangeEvent.emit(page);
  }

  protected getControl(name: string) {
    return this.form.get(name) as FormControl;
  }

  protected onPerPageChaneEvent(event: SelectChangeEvent) {
    if (this.saveInQuery()) {
      this.saveParamsInQuery({
        per_page: event.value.toString(),
        page: '1',
      });
    }

    this.pageChangeEvent.emit(1);
    this.perPageChangeEvent.emit(event.value);
  }

  private saveParamsInQuery(params: Record<string, string>) {
    const queryParams = {
      ...this.route.snapshot.queryParams,
      ...params,
    };

    this.router.navigate(['.'], {
      relativeTo: this.route,
      queryParams,
    });
  }
}
